import React, { useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { de as deLocale, enGB as enLocale } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * DatePicker — shadcn Popover + Calendar. `value` is an ISO date string
 * ("YYYY-MM-DD") or null.
 *
 * `required` (default false):
 *   - false → react-day-picker allows same-day toggle-off. `onChange`
 *     receives `null` on the second click of the same date. Consumers
 *     that want "clear on same-day click" (e.g. Orders list filter) can
 *     map `null → ""`.
 *   - true  → toggle-off disabled. Same-day click is a no-op. Use this
 *     where the field is mandatory (e.g. Availability Detail range).
 */
export function DatePicker({ value, onChange, disabled, placeholder, testId, required = false, minDate }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);

  const lng = i18n.language?.startsWith("en") ? enLocale : deLocale;
  const parsed = value ? parseISO(value) : null;
  const dateObj = parsed && isValid(parsed) ? parsed : undefined;

  const label = dateObj
    ? format(dateObj, i18n.language?.startsWith("en") ? "PP" : "dd.MM.yyyy", { locale: lng })
    : placeholder || t("common.select_date");

  const handleSelect = (d) => {
    if (d && isValid(d)) {
      onChange(format(d, "yyyy-MM-dd"));
    } else {
      onChange(null);
    }
    setOpen(false);
  };

  // React-day-picker `disabled` matcher — accepts `{ before: Date }` or a
  // function. When `minDate` is passed we use it verbatim so callers can
  // enforce e.g. "no past dates" or "until >= from".
  const calendarDisabled = minDate ? { before: minDate } : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          aria-label={label}
          className="h-10 w-full justify-start border-border-subtle bg-surface text-left font-normal text-text-primary hover:bg-offwhite"
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-text-secondary" aria-hidden />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto border-border-subtle bg-surface p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          mode="single"
          required={required}
          selected={dateObj}
          onSelect={handleSelect}
          defaultMonth={dateObj}
          locale={lng}
          disabled={calendarDisabled}
        />
      </PopoverContent>
    </Popover>
  );
}
